import { useCallback, useEffect, useState } from 'react'

import { Card, TextArea } from '@joor/design-system'
import { typography } from '@joor/design-system'
import debounce from 'lodash/debounce'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'

import { OrderStatus } from '__generated__/atlas-types'

import { useRenderToasters } from 'hooks/useRenderToasters'
import { isEditApprovedShippedRestricted as isEditApprovedShippedRestrictedSelector } from 'store/currentUser/selectors'

import { OrderData } from '../../Overview'
import { LoadingNotesWidget } from './LoadingNotesWidget'
import { useUpdatePriceChanges } from './notesWidget.mutations'
import { ReviewPageIds } from 'features/Orders/Review/review.ids'

export enum NotesWidgetTestIds {
  LoadingNotesWidget = 'loading-notes-widget',
}

const StyledCard = styled(Card)`
  section {
    margin: 0;
    padding: 18px 16px 16px 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    position: relative;
  }
`

const StyledTextArea = styled(TextArea)`
  font-size: 12px !important;
`

const StyledSaved = styled.p`
  position: absolute;
  top: 21px;
  right: 20px;
  color: var(--color-primary-400);
  ${typography.alpha.primaryActive.caption}
`

type Props = {
  className?: string
  testId?: string
  isEditable?: boolean
  id: OrderData['id']
  internalComments: OrderData['internalComments']
  status: OrderData['status']
  loading?: boolean
}

export const NotesWidget = ({
  className = '',
  testId,
  isEditable,
  id,
  internalComments,
  status,
  loading,
}: Props) => {
  const { updateOrderInternalComment } = useUpdatePriceChanges()
  const { renderErrorToast } = useRenderToasters()
  const [value, setValue] = useState(internalComments)
  const [showSaved, setShowSaved] = useState(false)
  const isEditApprovedShippedRestricted = useSelector(
    isEditApprovedShippedRestrictedSelector,
  )
  const restrictEditByOrderStatus =
    (status === OrderStatus.APPROVED || status === OrderStatus.SHIPPED) &&
    isEditApprovedShippedRestricted
  const canEdit = isEditable && !restrictEditByOrderStatus

  useEffect(() => {
    saveNotes(value)
  }, [value, id])

  const saveNotes = useCallback(
    debounce(async (value) => {
      if (!value || internalComments === value) {
        return
      }
      if (id) {
        updateOrderInternalComment(id, value)
          .then(() => {
            setShowSaved(true)
            setTimeout(() => {
              setShowSaved(false)
            }, 3000)
          })
          .catch(() => {
            renderErrorToast({
              title: 'Error',
              description: 'Internal notes could not be saved.',
            })
          })
      }
    }, 2500),
    [id],
  )

  // we want to skip the case when it tries to render with the order loaded
  // but 'value' is still outdated. this works because when you call
  // setValue inside the condition it skips the current render
  const shouldSkipRender = !loading && id && value === undefined

  if (shouldSkipRender) {
    setValue(internalComments)
  }

  if (loading) {
    return (
      <StyledCard
        testId={NotesWidgetTestIds.LoadingNotesWidget}
        className={className}
        title="Internal Notes"
      >
        <LoadingNotesWidget />
      </StyledCard>
    )
  }

  return (
    <StyledCard className={className} testId={testId} title="Internal Notes">
      {showSaved && <StyledSaved>Saved</StyledSaved>}
      <StyledTextArea
        label="Internal Notes"
        placeholder="Enter Note"
        caption="This note will be visible to internal users only."
        value={!value ? undefined : value}
        onInput={(e) => setValue(e.currentTarget.value)}
        maxLength={2500}
        disabled={!canEdit}
        id={ReviewPageIds.InternalNotesInputId}
      />
    </StyledCard>
  )
}
